import { useEffect, useState, type FormEvent } from 'react';
import { Activity, Box, Layers, MessageSquareText, Settings, TerminalSquare, Wrench } from 'lucide-react';
import { clearManagementToken, managementAuthRequiredEvent, persistManagementToken, readManagementToken, type ManagementAuthRequiredDetail } from './auth/managementAuth';
import { ConsoleShell, type NavItem } from './components/ConsoleShell';
import { ResourceHeader, StatusStrip } from './components/ResourceDetail';
import { personaListCopy } from './personas/copy';
import { PersonaListPage } from './personas/PersonaListPage';
import { PersonaWorkbenchPage } from './personaWorkbench/PersonaWorkbenchPage';
import { PromptAssignmentsPage, PromptControlPlanePage, PromptModuleDetailPage } from './promptControlPlane/PromptControlPlanePage';
import { LoadedSkillDetailPage, LoadedSkillsPage, MCPPresetsPage, MCPServerDetailPage, MCPServersPage, SkillDetailPage, SkillLibraryPage, ToolProxyPage } from './resourceManagement/ResourceManagementPage';
import { RuntimeAuditHistoryPage, RuntimeEngagementDetailPage, RuntimeEvidencePage, RuntimeProfileEvidencePage, RuntimeSessionDetailPage } from './runtimeEvidence/RuntimeEvidencePage';
import { useHashRoute } from './router/hashRoute';
import { SystemSettingsPage } from './systemSettings/SystemSettingsPage';
import { I18nProvider, supportedLocales, useI18n, useVisibleCopy } from './i18n';

const navItems: NavItem[] = [
  { id: 'overview', labelKey: 'nav.overview', icon: Activity, path: '#/' },
  { id: 'personas', labelKey: 'nav.personas', icon: Box, path: '#/personas' },
  { id: 'prompts', labelKey: 'nav.prompts', icon: MessageSquareText, path: '#/prompts' },
  { id: 'skills', labelKey: 'nav.skills', icon: Layers, path: '#/skills' },
  { id: 'tools', labelKey: 'nav.tools', icon: Wrench, path: '#/tools' },
  { id: 'runtime', labelKey: 'nav.runtime', icon: TerminalSquare, path: '#/runtime' },
  { id: 'system', labelKey: 'nav.system', icon: Settings, path: '#/system' },
];

export function App() {
  return (
    <I18nProvider>
      <ConsoleRoutes />
    </I18nProvider>
  );
}

function ConsoleRoutes() {
  const route = useHashRoute();
  const [token, setToken] = useState(() => readManagementToken());
  const [authMessage, setAuthMessage] = useState('');

  useEffect(() => {
    const onAuthRequired = (event: Event) => {
      const detail = (event as CustomEvent<ManagementAuthRequiredDetail>).detail;
      clearManagementToken();
      setToken(null);
      setAuthMessage(detail?.message ?? '');
    };
    window.addEventListener(managementAuthRequiredEvent, onAuthRequired);
    return () => window.removeEventListener(managementAuthRequiredEvent, onAuthRequired);
  }, []);

  const login = (nextToken: string) => {
    const persistedToken = persistManagementToken(nextToken);
    if (!persistedToken) return false;
    setToken(persistedToken);
    setAuthMessage('');
    return true;
  };

  const logout = () => {
    clearManagementToken();
    setToken(null);
    setAuthMessage('');
  };

  if (!token) return <LoginShell authMessage={authMessage} onLogin={login} />;

  return (
    <ConsoleShell navItems={navItems} activeNav={activeNav(route.path)} onLogout={logout}>
      {route.match.kind === 'persona-list'
        ? <PersonaListPage />
        : route.match.kind === 'persona-detail-alias'
          ? <PersonaDetailRedirect personaId={route.match.personaId} redirectTo={route.match.redirectTo} />
          : route.match.kind === 'persona-workbench'
            ? <PersonaWorkbenchPage personaId={route.match.personaId} initialTab={route.query.get('tab')} />
          : route.match.kind === 'prompt-module-list'
            ? <PromptControlPlanePage />
            : route.match.kind === 'prompt-module-detail'
              ? <PromptModuleDetailPage moduleId={route.match.moduleId} initialTab={route.query.get('tab')} initialAction={route.query.get('action')} />
            : route.match.kind === 'prompt-assignments'
              ? <PromptAssignmentsPage personaId={route.match.personaId} platform={route.query.get('platform')} />
              : route.match.kind === 'runtime-evidence'
                ? <RuntimeEvidencePage initialTab={route.query.get('tab')} />
                : route.match.kind === 'runtime-engagement-detail'
                  ? <RuntimeEngagementDetailPage engagementId={route.match.engagementId} initialTab={route.query.get('tab')} />
                  : route.match.kind === 'runtime-session-detail'
                    ? <RuntimeSessionDetailPage sessionId={route.match.sessionId} initialTab={route.query.get('tab')} />
                    : route.match.kind === 'runtime-profile-detail'
                      ? <RuntimeProfileEvidencePage sessionId={route.match.sessionId} initialTab={route.query.get('tab')} />
                      : route.match.kind === 'runtime-audit-list'
                        ? <RuntimeAuditHistoryPage initialTab={route.query.get('tab')} />
                        : route.match.kind === 'runtime-audit-detail'
                          ? <RuntimeAuditHistoryPage eventId={route.match.eventId} initialTab={route.query.get('tab')} />
                        : route.match.kind === 'skill-library-list'
                          ? <SkillLibraryPage />
                          : route.match.kind === 'skill-library-detail'
                            ? <SkillDetailPage skillId={route.match.skillId} initialTab={route.query.get('tab')} />
                            : route.match.kind === 'loaded-skills-list'
                              ? <LoadedSkillsPage />
                              : route.match.kind === 'loaded-skill-detail'
                                ? <LoadedSkillDetailPage personaId={route.match.personaId} skillName={route.match.skillName} initialTab={route.query.get('tab')} />
                                : route.match.kind === 'mcp-server-list'
                                  ? <MCPServersPage />
                                  : route.match.kind === 'mcp-server-detail'
                                    ? <MCPServerDetailPage mcpId={route.match.mcpId} initialTab={route.query.get('tab')} />
                                    : route.match.kind === 'mcp-preset-list'
                                      ? <MCPPresetsPage initialTab={route.query.get('tab')} />
                                      : route.match.kind === 'mcp-preset-detail'
                                        ? <MCPPresetsPage presetName={route.match.presetName} initialTab={route.query.get('tab')} />
                                        : route.match.kind === 'tool-proxy-list'
                                          ? <ToolProxyPage initialTab={route.query.get('tab')} />
                                          : route.match.kind === 'tool-proxy-detail'
                                            ? <ToolProxyPage personaId={route.match.personaId} serverName={route.match.serverName} initialTab={route.query.get('tab')} />
                                            : route.match.kind === 'system-settings'
                                              ? <SystemSettingsPage view="settings" initialTab={route.query.get('tab')} />
                                              : route.match.kind === 'system-status'
                                                ? <SystemSettingsPage view="status" initialTab={route.query.get('tab')} />
                                                : route.match.kind === 'release-operations'
                                                  ? <SystemSettingsPage view="release-operations" initialTab={route.query.get('tab')} />
                        : <FoundationHome />}
    </ConsoleShell>
  );
}

function activeNav(path: string) {
  if (path.startsWith('/personas')) return 'personas';
  if (path.startsWith('/prompts')) return 'prompts';
  if (path.startsWith('/skills')) return 'skills';
  if (path.startsWith('/tools')) return 'tools';
  if (path.startsWith('/runtime')) return 'runtime';
  if (path.startsWith('/system') || path.startsWith('/settings')) return 'system';
  return 'overview';
}

function LoginShell({ authMessage, onLogin }: { authMessage: string; onLogin: (token: string) => boolean }) {
  const { locale, setLocale, t } = useI18n();
  const ui = useVisibleCopy();
  const [tokenValue, setTokenValue] = useState('');
  const [validationMessage, setValidationMessage] = useState('');

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!onLogin(tokenValue)) {
      setValidationMessage(t('auth.required'));
      return;
    }
    setValidationMessage('');
  };

  return (
    <main className="login-shell">
      <section className="login-card">
        <div className="brand login-brand">
          <span className="brand-mark">cc</span>
          <div>
            <strong>{t('app.title')}</strong>
            <small>{t('app.subtitle')}</small>
          </div>
        </div>
        <div>
          <p className="eyebrow">{t('auth.eyebrow')}</p>
          <h1>{t('auth.title')}</h1>
          <p className="resource-description">{t('auth.description')}</p>
        </div>
        {authMessage ? <div className="notice notice-warning"><strong>{t('auth.reauthTitle')}</strong> {ui(authMessage)}</div> : null}
        {validationMessage ? <div className="notice notice-warning">{validationMessage}</div> : null}
        <form className="login-form" onSubmit={submit}>
          <label className="field-label">
            {t('auth.tokenLabel')}
            <input
              autoComplete="off"
              name="management-token"
              placeholder={t('auth.tokenPlaceholder')}
              type="password"
              value={tokenValue}
              onChange={event => setTokenValue(event.target.value)}
            />
          </label>
          <button className="btn btn-primary" type="submit">{t('auth.submit')}</button>
        </form>
        <div className="language-switch login-language" aria-label={t('app.language')}>
          {supportedLocales.map(option => (
            <button
              key={option.code}
              aria-pressed={option.code === locale}
              className={option.code === locale ? 'language-option active' : 'language-option'}
              type="button"
              onClick={() => setLocale(option.code)}
            >
              {option.shortLabel}
            </button>
          ))}
        </div>
      </section>
    </main>
  );
}

function FoundationHome() {
  const { t } = useI18n();

  return (
    <div className="page-stack">
      <ResourceHeader
        eyebrow={t('home.eyebrow')}
        title={t('home.title')}
        description={t('home.description')}
        meta={[
          [t('resource.surface'), 'Console v2'],
          [t('resource.mode'), t('status.readOnlyPreview')],
          [t('resource.api'), '/api/v1'],
        ]}
      />

      <StatusStrip
        items={[
          { label: t('status.routing'), value: 'Console v2', tone: 'green', evidence: t('status.hashRoutes') },
          { label: t('status.resources'), value: t('status.typed'), tone: 'blue', evidence: t('status.sharedComponents') },
          { label: t('status.api'), value: t('status.sharedClient'), tone: 'green', evidence: '/api/v1' },
          { label: t('status.mode'), value: t('status.readOnly'), tone: 'muted', evidence: t('status.noMutationYet') },
        ]}
      />

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>{t('home.resourcesTitle')}</h2>
            <p>{t('home.resourcesDescription')}</p>
          </div>
        </div>
        <div className="target-grid">
          <TargetCard title={t('nav.personas')} status={t('status.available')} href="#/personas" />
          <TargetCard title={t('home.targets.promptControlPlane')} status={t('status.available')} href="#/prompts" />
          <TargetCard title={t('home.targets.skillLibrary')} status={t('status.available')} href="#/skills" />
          <TargetCard title={t('home.targets.toolsMcp')} status={t('status.available')} href="#/tools" />
          <TargetCard title={t('home.targets.runtimeEvidence')} status={t('status.available')} href="#/runtime" />
          <TargetCard title={t('home.targets.auditHistory')} status={t('status.available')} href="#/runtime/audit" />
          <TargetCard title={t('home.targets.systemStatus')} status={t('home.targetStatus.livePartialEvidence')} href="#/system/status" />
          <TargetCard title={t('home.targets.system')} status={t('status.readOnly')} href="#/system" />
        </div>
      </section>
    </div>
  );
}

function PersonaDetailRedirect({ personaId, redirectTo }: { personaId: string; redirectTo: string }) {
  const { t } = useI18n();

  useEffect(() => {
    window.location.replace(redirectTo);
  }, [redirectTo]);

  return (
    <div className="page-stack">
      <ResourceHeader
        eyebrow={t(personaListCopy.key('redirect.eyebrow'))}
        title={t(personaListCopy.key('redirect.title'))}
        description={t(personaListCopy.key('redirect.description'), { personaId })}
        meta={[
          [t(personaListCopy.key('redirect.persona')), personaId],
          [t(personaListCopy.key('redirect.canonicalRoute')), redirectTo],
          [t(personaListCopy.key('redirect.mode')), t(personaListCopy.key('redirect.modeValue'))],
        ]}
        actions={<a className="btn" href={redirectTo}>{t(personaListCopy.key('redirect.openWorkbench'))}</a>}
      />
    </div>
  );
}

function TargetCard({ title, status, href }: { title: string; status: string; href?: string }) {
  const content = (
    <>
      <span className="target-icon"><Layers size={18} /></span>
      <strong>{title}</strong>
      <small>{status}</small>
    </>
  );
  return href ? <a className="target-card" href={href}>{content}</a> : <div className="target-card">{content}</div>;
}
